<script lang="ts">
	import { onMount } from 'svelte';
	import { prefersReducedMotion } from '$lib/utils/motion';

	interface Props {
		class?: string;
	}

	let { class: className = '' }: Props = $props();
	let canvas: HTMLCanvasElement;
	let animationId: number;

	// Layout constants
	const LAYOUT = {
		lines: { top: 0.28, bottom: 0.62 },
		hub: { x: 0.78, y: 0.45 },
		sensors: [
			{ x: 0.12, line: 0 },
			{ x: 0.28, line: 1 },
			{ x: 0.44, line: 0 },
			{ x: 0.58, line: 1 }
		]
	} as const;

	// Animation constants
	const PULSE_INTERVAL_FRAMES = 300;
	const PULSE_SPEED = 0.006;
	const LINE_SPEEDS = { top: 1.5, bottom: 1.3 };
	const GLOW_DECAY = 0.96;

	// Colors
	const COLORS = {
		line: 'rgba(34, 197, 94, 0.2)',
		connection: 'rgba(100, 116, 139, 0.05)',
		pulse: 'rgba(56, 189, 248, 0.6)',
		sensor: 'rgba(148, 163, 184, 0.5)',
		hubFill: 'rgba(30, 41, 59, 0.6)',
		hubStroke: 'rgba(56, 189, 248,',
		hubGlow: 'rgba(56, 189, 248,',
		hubBar: 'rgba(56, 189, 248,'
	} as const;

	onMount(() => {
		if (prefersReducedMotion()) return;

		const ctx = canvas.getContext('2d');
		if (!ctx) return;

		let width = canvas.offsetWidth;
		let height = canvas.offsetHeight;
		const dpr = Math.min(window.devicePixelRatio || 1, 2);

		canvas.width = width * dpr;
		canvas.height = height * dpr;
		ctx.scale(dpr, dpr);

		// Two heartbeat lines
		const lines = [
			{ y: height * LAYOUT.lines.top, offset: 0, speed: LINE_SPEEDS.top },
			{ y: height * LAYOUT.lines.bottom, offset: Math.PI, speed: LINE_SPEEDS.bottom }
		];

		// Sensors on each line
		const sensors = LAYOUT.sensors.map((s) => ({ xPct: s.x, line: s.line }));

		// Hub position
		const hub = { xPct: LAYOUT.hub.x, y: height * LAYOUT.hub.y, glow: 0 };

		// Occasional data pulse
		let pulse: { from: number; progress: number } | null = null;
		let pulseTimer = 0;

		function getEcgY(x: number, line: (typeof lines)[0]): number {
			const phase = (x * 0.02 + line.offset) % (Math.PI * 2);
			let y = 0;

			if (phase > 0.7 && phase < 0.85) {
				const t = (phase - 0.7) / 0.15;
				if (t < 0.4) y = -t * 20;
				else if (t < 0.6) y = -8 + (t - 0.4) * 160;
				else y = 24 - (t - 0.6) * 100;
			} else if (phase > 0.85 && phase < 1.1) {
				y = Math.sin(((phase - 0.85) * Math.PI) / 0.25) * 10;
			}

			return line.y + y;
		}

		function animate() {
			if (!ctx) return;
			ctx.clearRect(0, 0, width, height);

			// Update line offsets
			lines.forEach((line) => {
				line.offset += line.speed * 0.008;
			});

			// Draw heartbeat lines
			lines.forEach((line) => {
				ctx.beginPath();
				ctx.strokeStyle = COLORS.line;
				ctx.lineWidth = 1.5;

				for (let x = 0; x < width; x += 3) {
					const y = getEcgY(x, line);
					if (x === 0) ctx.moveTo(x, y);
					else ctx.lineTo(x, y);
				}
				ctx.stroke();
			});

			// Draw subtle connection lines to hub
			const hubX = width * hub.xPct;
			sensors.forEach((sensor) => {
				const sensorX = width * sensor.xPct;
				const sensorY = getEcgY(sensorX, lines[sensor.line]);

				ctx.beginPath();
				ctx.strokeStyle = COLORS.connection;
				ctx.setLineDash([2, 4]);
				ctx.moveTo(sensorX, sensorY);
				ctx.lineTo(hubX, hub.y);
				ctx.stroke();
				ctx.setLineDash([]);
			});

			// Handle data pulse
			pulseTimer++;
			if (!pulse && pulseTimer > PULSE_INTERVAL_FRAMES) {
				pulseTimer = 0;
				pulse = { from: Math.floor(Math.random() * sensors.length), progress: 0 };
			}

			if (pulse) {
				pulse.progress += PULSE_SPEED;

				const sensor = sensors[pulse.from];
				const fromX = width * sensor.xPct;
				const fromY = getEcgY(fromX, lines[sensor.line]);
				const x = fromX + (hubX - fromX) * pulse.progress;
				const y = fromY + (hub.y - fromY) * pulse.progress;

				// Simple dot
				ctx.beginPath();
				ctx.fillStyle = COLORS.pulse;
				ctx.arc(x, y, 4, 0, Math.PI * 2);
				ctx.fill();

				if (pulse.progress >= 1) {
					hub.glow = 1;
					pulse = null;
				}
			}

			// Decay hub glow
			hub.glow *= GLOW_DECAY;

			// Draw sensors (simple dots on the lines)
			sensors.forEach((sensor) => {
				const x = width * sensor.xPct;
				const y = getEcgY(x, lines[sensor.line]);

				ctx.beginPath();
				ctx.arc(x, y, 4, 0, Math.PI * 2);
				ctx.fillStyle = COLORS.sensor;
				ctx.fill();
			});

			// Draw hub (simple rectangle with glow effect)
			const hubW = 40;
			const hubH = 28;

			// Subtle glow when data arrives
			if (hub.glow > 0.05) {
				ctx.beginPath();
				ctx.roundRect(hubX - hubW / 2 - 4, hub.y - hubH / 2 - 4, hubW + 8, hubH + 8, 6);
				ctx.fillStyle = `${COLORS.hubGlow} ${hub.glow * 0.15})`;
				ctx.fill();
			}

			ctx.beginPath();
			ctx.roundRect(hubX - hubW / 2, hub.y - hubH / 2, hubW, hubH, 4);
			ctx.fillStyle = COLORS.hubFill;
			ctx.fill();
			ctx.strokeStyle = `${COLORS.hubStroke} ${0.3 + hub.glow * 0.4})`;
			ctx.lineWidth = 1;
			ctx.stroke();

			// Simple static bars inside hub
			const barW = 5;
			const heights = [8, 12, 10, 14];
			heights.forEach((h, i) => {
				ctx.fillStyle = `${COLORS.hubBar} ${0.4 + hub.glow * 0.3})`;
				ctx.fillRect(hubX - 14 + i * 8, hub.y + hubH / 2 - 4 - h, barW, h);
			});

			animationId = requestAnimationFrame(animate);
		}

		animate();

		const handleResize = () => {
			width = canvas.offsetWidth;
			height = canvas.offsetHeight;
			canvas.width = width * dpr;
			canvas.height = height * dpr;
			ctx.scale(dpr, dpr);

			lines[0].y = height * LAYOUT.lines.top;
			lines[1].y = height * LAYOUT.lines.bottom;
			hub.y = height * LAYOUT.hub.y;
		};

		window.addEventListener('resize', handleResize);

		return () => {
			cancelAnimationFrame(animationId);
			window.removeEventListener('resize', handleResize);
		};
	});
</script>

<canvas bind:this={canvas} class="w-full h-full {className}" aria-hidden="true"></canvas>
